import { useCallback } from 'react';

import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useUIStoreActions } from '@atlassian/conversation-assistant-store/controllers/ui-store';

/**
 * Returns an imperative callback that resets every store slice associated with
 * being inside an entity-scoped chat:
 *
 * - `selectedSpace`                                          (ui-store)
 * - `selectedPinnedItem`                                     (ui-store)
 * - `emptyChatLaunch.*` (customGreeting, customConversationActions,
 *   replaceProviderActions, projectContext, skillRegistryTags)  (ui-store)
 * - `aiFeatureContext.projectContext`                        (chat-context store)
 * - `aiFeatureContext.pinned_object_context`                 (chat-context store)
 *
 * Callers: header back button, side-nav conversation click, side-nav new-chat
 * click, and any future surface that exits a Rovo Space.
 *
 * Without this cleanup, stale space-launch state persists after exiting a
 * space — the empty-state keeps rendering the previous space's quick actions
 * (because `emptyChatLaunch.replaceProviderActions` is still `true`), and the
 * next-sent message still carries the previous space's `space_id`
 * (from `aiFeatureContext.projectContext`).
 */
export const useExitSpace = (): (() => void) => {
	const { clearSelectedSpace, clearSelectedPinnedItem, clearEmptyChatLaunchAll } =
		useUIStoreActions();
	const { setAIFeatureContext } = useChatContextStoreActions();

	return useCallback(() => {
		clearSelectedSpace();
		clearSelectedPinnedItem();
		clearEmptyChatLaunchAll();
		// Clear the projectContext key. The store's setAIFeatureContext writes the
		// return value of setContext under aiFeatureContext[contextKey], and its
		// getAIFeatureContext cleanup then strips any key whose value is `undefined`.
		// The store's setContext signature is `<T>(x: T) => T`, so the `undefined`
		// return needs a narrow cast; we deliberately return undefined rather than
		// forging a same-shape empty object that downstream consumers would
		// misinterpret (see PR review feedback re: `({}) as T` being unsound).
		setAIFeatureContext({
			contextKey: 'projectContext',
			setContext: <T>(_: T): T => undefined as T,
		});
		setAIFeatureContext({
			contextKey: 'pinned_object_context',
			setContext: <T>(_: T): T => undefined as T,
		});
	}, [clearSelectedSpace, clearSelectedPinnedItem, clearEmptyChatLaunchAll, setAIFeatureContext]);
};
